import { BaseTextInput } from "@/components/BaseTextInput/BaseTextInput";
import { EditProfileFormValues } from "@/features/profile/types/EditProfileTypes";
import { FC } from "react";
import { language } from "@/utils/constants/languages";
import i18next from "i18next";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "@/routes/siteRoutesList";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import {
  Flex,
  TextField,
  Text,
  Button,
  Spinner,
  TextArea,
  Checkbox,
  Select,
  Box,
} from "@radix-ui/themes";

export const EditProfileForm: FC<{
  editProfileValues: EditProfileFormValues;
  setFieldValue: any;
  errors: any;
  saveChanges: any;
  isSubmitting: boolean;
  hasNotChanged: boolean;
  setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
}> = ({
  editProfileValues,
  setFieldValue,
  errors,
  saveChanges,
  isSubmitting,
  hasNotChanged,
  setCurrentTab,
}) => {
  const currentUser = useCurrentUserProfile((s) => s.user);
  const navigate = useNavigate();

  const isDiscordUser = currentUser?.authenticationMethod === "discord";
  const isTwitchUser = currentUser?.authenticationMethod === "twitch";

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <Flex direction="column" gap="4">
          {/* Username */}
          <Flex direction="column" gap="1">
            <Text as="label" size="2">
              {i18next.t("strings.user_name")}
            </Text>
            <TextField.Root
              placeholder={i18next.t("strings.user_name_placeholder")}
              size="3"
              type="text"
              value={editProfileValues.userName.trim()}
              onChange={(e) => {
                setFieldValue("userName", e.target.value);
              }}
              disabled={isDiscordUser || isTwitchUser}
            />
          </Flex>

          {/* Full name*/}
          <Flex direction="column" gap="1">
            <Text as="label" size="2">
              {i18next.t("strings.full_name")}
            </Text>
            <TextField.Root
              placeholder={i18next.t("strings.full_name_placeholder")}
              size="3"
              type="text"
              value={editProfileValues.fullName}
              onChange={(e) => {
                setFieldValue("fullName", e.target.value);
              }}
            />
          </Flex>

          {/* Profile description */}
          <Flex direction="column" gap="1">
            <Text as="label" size="2">
              {i18next.t("strings.about")}
            </Text>

            <TextArea
              placeholder={i18next.t("strings.about_placeholder")}
              size="3"
              value={editProfileValues.profileDescription}
              onChange={(e) => {
                setFieldValue("profileDescription", e.target.value);
              }}
            />
          </Flex>

          {/* FOR EVENT, ADD FIELD FOR EMAIL */}
          {(brand.event.isEvent && brand.authMethod === "email") ||
          (brand.event.isEvent && brand.authMethod === "emailNoVerify") ? (
            <Flex direction="column" gap="1">
              <Text as="label" size="2">
                Email
              </Text>
              <TextField.Root
                placeholder="Your email address"
                size="3"
                type="text"
                value={editProfileValues.email}
                onChange={(e) => {
                  setFieldValue("email", e.target.value);
                }}
                disabled
              />
            </Flex>
          ) : (
            ""
          )}

          <Flex gap="2" align="center">
            <Checkbox
              checked={editProfileValues.termsAccepted}
              onCheckedChange={(e) => {
                setFieldValue("termsAccepted", e);
              }}
            />
            <Text size="2" as="label">
              {i18next.t("settings.terms")}
            </Text>
          </Flex>

          {brand.hasFeature.haslanguagePicker && (
            <Flex direction="column" gap="2">
              <Text size="2" as="label">
                {i18next.t("settings.tabs.otherSettings")}
              </Text>

              <Select.Root
                defaultValue={editProfileValues.lang}
                value={editProfileValues.lang}
                onValueChange={(e) => {
                  setFieldValue("lang", e);
                }}
              >
                {" "}
                <Select.Trigger />
                <Select.Content>
                  {language.map((lang) => {
                    if (lang.value === editProfileValues.lang) {
                      return (
                        <Select.Item value={lang.value} key={lang.value}>
                          {lang.label}
                        </Select.Item>
                      );
                    }
                  })}
                  {language.map((lang) => {
                    if (lang.value !== editProfileValues.lang) {
                      return (
                        <Select.Item
                          value={lang.value}
                          key={lang.value}
                          onClick={() => {
                            setFieldValue("lang", lang.value);
                          }}
                        >
                          {lang.label}
                        </Select.Item>
                      );
                    }
                  })}
                </Select.Content>
              </Select.Root>
            </Flex>
          )}

          <Flex justify="between">
            <Button
              variant="outline"
              onClick={() => navigate(siteRoutes["profile"])}
            >
              {i18next.t("buttons.cancel")}
            </Button>

            <Button
              variant="solid"
              onClick={saveChanges}
              disabled={isSubmitting || hasNotChanged}
            >
              {isSubmitting ? <Spinner /> : i18next.t("buttons.save_changes")}
            </Button>
          </Flex>
        </Flex>
      </form>
    </>
  );
};
